import { createRouter, createWebHistory } from 'vue-router'
import ogImageUrl from './assets/drawtalk.png'
import { l, lang } from './libs/localization'

const siteName = `${l('ブラウザで遊べる絵チャット')}${l('（令和最新版）')}`
const defaultDescription = l('お絵描きチャット - 高機能なキャンバスで本格的なイラストを描いたり、チャットやスタンプで交流を楽しもう！')
const pageTitle = (title: Parameters<typeof l>[0]) => l('{page}｜{site}', { page: l(title), site: siteName })
const japaneseOrigin = 'https://drawtalk.laineus.com'
const englishOrigin = 'https://en-drawtalk.laineus.com'

const routes = [
  {
    path: '/',
    name: 'Top',
    component: () => import('./components/Top.vue'),
    meta: {
      title: siteName,
      description: defaultDescription
    }
  },
  {
    path: '/users/:userId',
    name: 'User',
    component: () => import('./components/Top.vue')
  },
  {
    path: '/rooms/:roomId',
    name: 'Room',
    component: () => import('./components/Room.vue')
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('./components/Terms.vue'),
    meta: {
      localized: false,
      title: pageTitle('利用規約'),
      description: l('{site}の利用規約です。', { site: siteName })
    }
  },
  {
    path: '/privacy_policy',
    name: 'PrivacyPolicy',
    component: () => import('./components/PrivacyPolicy.vue'),
    meta: {
      localized: false,
      title: pageTitle('プライバシーポリシー'),
      description: l('{site}におけるユーザー情報の取り扱いについてご案内します。', { site: siteName })
    }
  },
  {
    path: '/account-deletion',
    name: 'AccountDeletion',
    component: () => import('./components/AccountDeletion.vue'),
    meta: {
      title: lang === 'ja' ? `アカウント削除｜${siteName}` : `Account deletion | ${siteName}`,
      description: lang === 'ja' ? `${siteName}のアカウントと関連データを削除する方法です。` : `Learn how to delete your ${siteName} account and associated data.`
    }
  },
  {
    path: '/child-safety-standards',
    name: 'ChildSafetyStandards',
    component: () => import('./components/ChildSafetyStandards.vue'),
    meta: {
      title: lang === 'ja' ? `児童の安全に関する基準｜${siteName}` : `Child Safety Standards | ${siteName}`,
      description: lang === 'ja' ? `${siteName}における児童の性的虐待と搾取に対する基準です。` : `Standards against child sexual abuse and exploitation for ${siteName}.`
    }
  },
  {
    path: '/faq',
    name: 'Faq',
    component: () => import('./components/Faq.vue'),
    meta: {
      localized: false,
      title: pageTitle('よくある質問'),
      description: l('キャンバス、アカウント、利用停止など、絵チャットに関するよくある質問をまとめています。')
    }
  },
  {
    path: '/guide/painter',
    name: 'PainterGuide',
    component: () => import('./components/PainterGuide.vue'),
    meta: {
      title: pageTitle('ペイントツールの使い方'),
      description: l('ペン、消しゴム、塗りつぶし、選択、レイヤーなど、絵チャットのペイントツールの使い方を紹介します。')
    }
  },
  {
    path: '/suspension-lift-request',
    name: 'SuspensionLiftRequest',
    component: () => import('./components/SuspensionLiftRequest.vue')
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('./components/admin/Admin.vue'),
    children: [
      {
        path: '',
        name: 'AdminHome',
        component: () => import('./components/admin/AdminHome.vue')
      },
      {
        path: 'users/:userId',
        name: 'AdminUserDetail',
        component: () => import('./components/admin/AdminUserDetail.vue')
      },
      {
        path: 'suspension-lift-requests',
        name: 'AdminSuspensionLiftRequests',
        component: () => import('./components/admin/SuspensionLiftRequests.vue')
      },
      {
        path: 'suspension-lift-requests/:requestId',
        name: 'AdminSuspensionLiftRequestDetail',
        component: () => import('./components/admin/SuspensionLiftRequestDetail.vue')
      }
    ]
  }
]
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior () {
    return { left: 0, top: 0 }
  }
})

const setCanonical = (url: string) => {
  const link = document.querySelector('link[rel="canonical"]') || document.createElement('link')
  link.setAttribute('rel', 'canonical')
  link.setAttribute('href', url)
  if (!document.head.contains(link)) {
    document.head.appendChild(link)
  }
}

const setAlternateLink = (hreflang: string, origin: string) => {
  const selector = `link[rel="alternate"][hreflang="${hreflang}"]`
  const link = document.querySelector<HTMLLinkElement>(selector) || document.createElement('link')
  const url = new URL(`${window.location.pathname}${window.location.search}`, origin)
  link.setAttribute('rel', 'alternate')
  link.setAttribute('hreflang', hreflang)
  link.setAttribute('href', url.href)
  if (!document.head.contains(link)) document.head.appendChild(link)
}

const setAlternateLinks = () => {
  setAlternateLink('ja', japaneseOrigin)
  setAlternateLink('en', englishOrigin)
  setAlternateLink('x-default', japaneseOrigin)
}

const removeAlternateLinks = () => {
  for (const hreflang of ['ja', 'en', 'x-default']) {
    document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`)?.remove()
  }
}

const setMetaContent = (selector: string, attribute: 'name' | 'property', key: string, content: string) => {
  const meta = document.querySelector<HTMLMetaElement>(selector) || document.createElement('meta')
  meta.setAttribute(attribute, key)
  meta.setAttribute('content', content)
  if (!document.head.contains(meta)) document.head.appendChild(meta)
}

router.afterEach(to => {
  const localized = to.meta.localized !== false
  const title = typeof to.meta.title === 'string' ? to.meta.title : siteName
  const description = typeof to.meta.description === 'string' ? to.meta.description : defaultDescription
  const pageUrl = localized
    ? window.location.href.replace('drawtalk2.laineus.com', 'drawtalk.laineus.com')
    : new URL(`${window.location.pathname}${window.location.search}`, japaneseOrigin).href
  const imageUrl = new URL(ogImageUrl, window.location.origin).href.replace('drawtalk2.laineus.com', 'drawtalk.laineus.com')

  document.title = title
  setMetaContent('meta[name="description"]', 'name', 'description', description)
  setMetaContent('meta[property="og:site_name"]', 'property', 'og:site_name', siteName)
  setMetaContent('meta[property="og:title"]', 'property', 'og:title', title)
  setMetaContent('meta[property="og:description"]', 'property', 'og:description', description)
  setMetaContent('meta[property="og:url"]', 'property', 'og:url', pageUrl)
  setMetaContent('meta[property="og:image"]', 'property', 'og:image', imageUrl)
  setMetaContent('meta[name="twitter:title"]', 'name', 'twitter:title', title)
  setMetaContent('meta[name="twitter:description"]', 'name', 'twitter:description', description)
  setMetaContent('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl)
  setCanonical(pageUrl)
  if (localized) setAlternateLinks()
  else removeAlternateLinks()
})

export default router
